import { defineStore } from 'pinia';
import { EModalName, type IModalParams } from '~/types/modals';
import { useStoreGtm } from './gtm';
import { EOverlayItemType, type IOverlayItem, useOverlayQueueStore } from './overlayQueue';
import type { IGtmPush } from '~/types/gtm';

export const useStoreModals = defineStore('abstractModals', {
  state: () => ({
    returnModal: null as EModalName | null,
    returnModalParams: null as Record<string, string | number> | null,
    autoSelectedFirstSavedMethod: false,
  }),
  getters: {
    isModalActive(): boolean {
      return useOverlayQueueStore().queue.some(
        (queueItem) => queueItem.type === EOverlayItemType.modal
      );
    },

    activeModal(): IOverlayItem | undefined {
      return useOverlayQueueStore().queue.find(
        (queueItem) => queueItem.type === EOverlayItemType.modal
      );
    },
  },

  actions: {
    /**
     * SHOULD BE PRIVATE
     * Add modal to queue, and ignore current active moda
     *
     * @param modalName - enum of modal window
     */
    addModal(modalName: EModalName, params?: IModalParams) {
      useOverlayQueueStore().addToOverlayQueue(
        {
          name: modalName,
          params: params || {},
          type: EOverlayItemType.modal,
          shouldOverlap: true,
          shouldBeAttchedToQuery: !params?.doNotAttachToQuery,
        },
        { force: true }
      );
    },

    /**
     * SHOULD BE PRIVATE
     * Replace active modal in queue
     *
     * @param modalName - enum of modal window
     */
    replaceModal(modalName: EModalName, params?: IModalParams) {
      useOverlayQueueStore().replaceOverlayQueueItem({
        name: modalName,
        params: params || {},
        type: EOverlayItemType.modal,
        shouldOverlap: true,
        shouldBeAttchedToQuery: !params?.doNotAttachToQuery,
      });
      this.setAutoSelectedFirstSavedMethod(false);
    },

    /**
     * SHOULD BE PRIVATE
     * Process modal window with special params
     * (e.g. 'closeAll' - close all another modals)
     *
     * @param modalName - enum of modal window
     * @param params - modal parameters object
     */
    processModal(modalName: EModalName, params?: IModalParams) {
      if (params?.closeAll) {
        delete params.closeAll;
        this.replaceModal(modalName, params);
      } else {
        this.addModal(modalName, params);
      }
      this.setAutoSelectedFirstSavedMethod(false);
    },

    /**
     * close modal window
     *
     * @param modalName - enum of modal window
     */
    closeModal(modalName: EModalName) {
      useOverlayQueueStore().removeFromOverlayQueue(modalName);
      this.setAutoSelectedFirstSavedMethod(false);
    },

    /**
     * close all modal windows
     *
     * @param modalName - enum of modal window
     */
    closeAllModals() {
      useOverlayQueueStore().removeFromOverlayQueueByType(EOverlayItemType.modal);
      this.setAutoSelectedFirstSavedMethod(false);
    },

    /**
     * Setting new modal params
     * e. g.: `/?deposit` -> `/?deposit&currency=BTC`
     */
    setModalParams(modalName: EModalName, modalParams: IModalParams) {
      const item = useOverlayQueueStore().queueMap[modalName];

      if (!item) {
        console.error('ERROR: Modal not found by name');
        return;
      }

      item.params = modalParams;

      useOverlayQueueStore().updateOverlayQueueItem(item);
    },

    /**
     * Update existing modal params
     * e. g.: `/?deposit&currency=USD` -> `/?deposit&currency=BTC`
     */
    updateModalParams(modalName: EModalName, modalParams: IModalParams) {
      const item = useOverlayQueueStore().queueMap[modalName];

      if (!item) {
        console.error('ERROR: Modal not found by name');
        return;
      }

      item.params = { ...item.params, ...modalParams };

      useOverlayQueueStore().updateOverlayQueueItem(item);
    },

    /**
     * MAIN METHOD
     * Open modal window with special params
     * (e.g. 'closeAll' - close all another modals)
     *
     * @param modalName - enum of modal window
     * @param params - modal parameters object
     */
    openModal(modalName: EModalName, params?: IModalParams) {
      const { gtmParams, ...otherParams } = params || {};
      switch (modalName) {
        case EModalName.login:
          this.processModal(modalName, otherParams);
          break;
        case EModalName.signup:
          if (gtmParams) {
            useStoreGtm().push({
              event: 'signup_button_click',
              with_flow_name: true,
              ...gtmParams,
            });
          }

          this.processModal(modalName, otherParams);
          break;
        case EModalName.deposit:
          if (gtmParams) {
            useStoreGtm().push({
              event: 'deposit_button_click',
              with_flow_name: true,
              ...gtmParams,
            });
          }

          this.processModal(modalName, otherParams);
          break;
        case EModalName.forgot:
        case EModalName.reset_password:
        case EModalName.unlock:
        case EModalName.deposit_currencies:
        case EModalName.confirm:
        case EModalName.unlock_result:
        case EModalName.freespins:
        case EModalName.bonuses:
        case EModalName.level_up:
        case EModalName.notification:
        case EModalName.welcome:
        case EModalName.cashier:
        case EModalName.compoints:
        case EModalName.buy_crypto:
        case EModalName.race:
        case EModalName.bonus_promo:
        case EModalName.estonian:
        case EModalName.cooling_off_period:
        default:
          this.processModal(modalName, otherParams);
          break;
      }
    },
    openLoginOrSignupModal(gtmParams?: Partial<IGtmPush>) {
      if (window && window.localStorage.getItem(HAD_SIGNIN_STORAGE_KEY)) {
        this.openModal(EModalName.login, { closeAll: true });
      } else {
        this.openModal(EModalName.signup, { closeAll: true, gtmParams });
      }
    },
    openPwaInstallModal() {
      if (window?.deferredPrompt) {
        window.deferredPrompt.prompt();
        window.deferredPrompt.userChoice.then((choiceResult) => {
          if (choiceResult.outcome === 'accepted') {
            window.deferredPrompt = null;
          } else {
            this.openModal(EModalName.pwa_install, { closeAll: true });
          }
          window.deferredPrompt = null; // Clear the deferred prompt
        });
        return;
      }
      this.openModal(EModalName.pwa_install, { closeAll: true });
    },

    setReturnModal(returnModal: EModalName, params?: Record<string, string | number>) {
      this.returnModal = returnModal;
      if (params) {
        this.returnModalParams = params;
      }
    },
    clearReturnModal() {
      this.returnModal = null;
      this.returnModalParams = null;
    },
    setAutoSelectedFirstSavedMethod(val: boolean) {
      this.autoSelectedFirstSavedMethod = val;
    },
  },
});
